import { useCreateMessageMutation } from './useCreateMessageMutation';
import { useMutation } from '@tanstack/react-query';
import { useCreateChatMutation } from './useCreateChatMutation';
import {
  AI_MESSAGE_SENDER,
  Chat,
  ChatMessage,
  ChatPromptAndAnswer,
  CLIENT_MESSAGE_SENDER,
  GptModel,
  NEW_CHAT_ID,
  PromptExtension,
} from '../../types';
import { LibraryPromptWizardState, useChatContext } from '../chatContext';
import { ComplexContent, stringToComplexContent } from '../components/TextMessageInput';
import {
  getFinalPrompt,
  getQuestionOrDefault,
  getTempMessageId,
  PREV_ANSWER_REFERENCE,
} from '../../util';
import { ANSWER_MESSAGE_STATUS_OK } from '../../growegyAIApi';

const calculate = (systemMessage: string | (() => string) | null | undefined) =>
  !systemMessage ? null : typeof systemMessage === 'function' ? systemMessage() : systemMessage;

export type SendMessageSource =
  | 'input'
  | 'sample'
  | 'retry'
  | 'library'
  | 'composer'
  | 'function'
  | 'business-plan-editor';
export const useSendMessageMutation = (args: {
  onChatCreated: (chat: Chat) => void;
  onChatCreationFailed: (args: { firstMessageContent: ComplexContent }) => void;
  onMessageCreated: () => void;
  gptModel: GptModel;
  systemMessage?: string | (() => string) | null;
  setComplexContent: (complexContent: ComplexContent) => void;
  libraryPromptWizardState: LibraryPromptWizardState | null;
  setLibraryPromptWizardState: (state: LibraryPromptWizardState | null) => void;
}) => {
  const {
    onChatCreated,
    onChatCreationFailed,
    onMessageCreated,
    gptModel,
    systemMessage,
    setComplexContent,
    libraryPromptWizardState,
    setLibraryPromptWizardState,
  } = args;
  const { setChatContext } = useChatContext();

  const { mutateAsync: createMessageMutation } = useCreateMessageMutation(
    onMessageCreated,
    onMessageCreated
  );

  const { mutateAsync: createChatMutation } = useCreateChatMutation(onChatCreated, (_, props) => {
    const { firstMessageContent } = props;
    onChatCreationFailed({ firstMessageContent });
    setChatContext(NEW_CHAT_ID, {
      rawContent: firstMessageContent.rawContent,
      plainTextContent: firstMessageContent.plainTextContent,
    });
  });

  return useMutation({
    mutationFn: async (variables: {
      chat: Chat;
      contentToSend: ComplexContent | null;
      source: SendMessageSource;
      messageClientId: string;
      promptExtensions?: PromptExtension[];
    }) => {
      const { chat, contentToSend, source, messageClientId, promptExtensions } = variables;
      if (!contentToSend && (!promptExtensions || promptExtensions.length === 0)) {
        return { chat, chatPromptAndAnswer: null };
      }
      let isWizardStep: boolean = false;
      let wizardStepNumber: number | undefined = undefined;
      let isFinalWizardStep: boolean = false;
      let answerContent: string | undefined = undefined;
      const currentStepIndex = libraryPromptWizardState?.currentStepState.index ?? 0;
      if (libraryPromptWizardState && currentStepIndex <= libraryPromptWizardState.steps.length) {
        isWizardStep = true;
        wizardStepNumber = currentStepIndex;
        isFinalWizardStep = currentStepIndex === libraryPromptWizardState.steps.length;
        if (isFinalWizardStep) {
          answerContent = 'Great! Here’s your final prompt:';
        } else {
          let newQuestion = libraryPromptWizardState.steps[currentStepIndex].question;
          if (newQuestion.indexOf(PREV_ANSWER_REFERENCE) >= 0 && currentStepIndex > 0) {
            newQuestion = newQuestion.replaceAll(
              PREV_ANSWER_REFERENCE,
              getQuestionOrDefault(contentToSend?.plainTextContent ?? '', false)
            );
          }
          answerContent = newQuestion;
        }
      }

      if (source === 'input') setComplexContent(stringToComplexContent(''));
      const resultingChat =
        chat.id === NEW_CHAT_ID
          ? await createChatMutation({
              firstMessageContent: contentToSend ?? stringToComplexContent(''),
              model: gptModel,
              type: chat.type,
              subtype: chat.subtype,
            })
          : chat;
      const chatIdToSendMessage = resultingChat.id;

      let chatPromptAndAnswer: ChatPromptAndAnswer | null = null;
      if (!libraryPromptWizardState || libraryPromptWizardState.steps.length > 0) {
        const clientId = isFinalWizardStep ? getTempMessageId() : messageClientId;
        const prompt: ChatMessage = {
          id: null,
          clientId,
          content: contentToSend?.plainTextContent ?? null,
          sender: CLIENT_MESSAGE_SENDER,
          timestamp: new Date(),
          isWizardStep,
          wizardStepNumber,
          promptId: libraryPromptWizardState?.prompt.id ?? null,
        };
        const answer: ChatMessage = {
          id: null,
          clientId,
          sender: AI_MESSAGE_SENDER,
          timestamp: new Date(),
          content: '',
          isPlaceholder: true,
          isWizardStep,
          wizardStepNumber,
          promptId: null,
        };
        chatPromptAndAnswer = await createMessageMutation({
          chatId: chatIdToSendMessage,
          promptAndAnswer: {
            prompt,
            promptExtensions,
            answer,
            answerStatus: ANSWER_MESSAGE_STATUS_OK,
          },
          systemMessage: calculate(systemMessage),
          answerContent,
        });
      }

      if (libraryPromptWizardState) {
        const newLibraryPromptWizardState: LibraryPromptWizardState = {
          ...libraryPromptWizardState,
          steps: libraryPromptWizardState.steps.map((s, i) =>
            currentStepIndex - 1 === i ? { ...s, answer: contentToSend?.plainTextContent ?? '' } : s
          ),
          currentStepState: {
            index: currentStepIndex + 1,
            adhocValues: [],
            selectedValues: [],
            addNewMode: false,
            newValueDescription: '',
          },
        };
        setLibraryPromptWizardState(newLibraryPromptWizardState);
        setChatContext(chatIdToSendMessage, {
          libraryPromptWizardState: newLibraryPromptWizardState,
        });

        if (isFinalWizardStep) {
          const finalPromptContent = getFinalPrompt(
            libraryPromptWizardState.prompt.content,
            newLibraryPromptWizardState.steps
          );
          setLibraryPromptWizardState(null);
          setChatContext(chatIdToSendMessage, { libraryPromptWizardState: null });

          chatPromptAndAnswer = await createMessageMutation({
            chatId: chatIdToSendMessage,
            systemMessage: calculate(systemMessage),
            promptAndAnswer: {
              prompt: {
                id: null,
                clientId: messageClientId,
                content: finalPromptContent,
                sender: CLIENT_MESSAGE_SENDER,
                timestamp: new Date(),
                promptId: libraryPromptWizardState.prompt.id,
              },
              answer: {
                id: null,
                clientId: messageClientId,
                content: '',
                sender: AI_MESSAGE_SENDER,
                timestamp: new Date(),
                isPlaceholder: true,
                promptId: null,
              },
              answerStatus: ANSWER_MESSAGE_STATUS_OK,
            },
          });
        }
      }
      return { chat: resultingChat, chatPromptAndAnswer };
    },
  });
};
